import { configStore, documentStore } from "@/store/store";
import {
  BuilderAction,
  BuilderActionData,
  BuilderActionNames
} from "@/types/elements";
import { breakdanceStoreActions } from "@/store/actions/store-actions";
import { addBuilderElementConditionalDependenciesToHead } from "@/lib/canvas/builder-element-dependencies";
import { findNode, parentNodesReduce } from "@/lib/tree/tree";
import { getHash } from "@/util/hash";
import registerColumnActions from "@/lib/columns/actions";
import defensiveCopy from "@/util/defensive-copy";
import { ActionArguments } from "@/types/store-actions";
import { renderTags, renderTwig } from "@/lib/twig/twig";
import { TreeNode } from "@/types/tree";
import { addOrRerunInlineScriptInHead } from "@/lib/canvas/assets/js-in-head";
import { getBuilderCanvasDocument } from "@/lib/canvas/helpers";
import { get } from "lodash";
import { getClassNameForNode } from "@/lib/elements/utils";
import { Tags } from "@/types/tags";
import { deepEqual } from "@/util/deep-equal";
import { getDesignForElementFromPreset } from "@/lib/presets/presets";

/*
  Register all the store actions added to elements in PHP
 */
export function registerAllStoreActions() {
  registerColumnActions();

  registerOnPropertyChange();
  registerOnAfterDeletedElement();
  registerOnMovedElement();
  registerOnMountedElement();
  registerOnCreatedElement();
  registerOnBeforeDeletingElement();
  registerOnActivatedElement();
}

/**
 * Does the same as dependencyMatch but instead of relying on the "path" we use the values/prevValues
 * to compensate for a partial "path", which we get for onPropertyChange when "pasting" all properties
 */
export function actionDependencyMatchByValues(
  dependencies: string[] | undefined,
  value: unknown,
  previousValue: unknown,
  // this is the path of the object we're getting the values from.
  // e.g the real full path is "design.size.width". But "objectPrependedPath" is "design" and "value" is  {size: {width: 10px}}
  objectPrependedPath: string
) {
  return !!dependencies?.find(dependency => {
    const dependencyWithoutPrependedPath = dependency.replace(
      objectPrependedPath.endsWith(".")
        ? objectPrependedPath
        : `${objectPrependedPath}.`,
      ""
    );
    const newValueAtPath = get(value, dependencyWithoutPrependedPath);
    const oldValueAtPath = get(previousValue, dependencyWithoutPrependedPath);

    if (!newValueAtPath && !oldValueAtPath) return false;

    return !deepEqual(newValueAtPath, oldValueAtPath);
  });
}

/***** PRIVATE API ****/

function getUniqueArrayOfActionsByName(
  actionName: BuilderActionNames
): BuilderAction[] {
  const actionsWithElements: BuilderAction[] = [];

  Object.values(configStore.elements).forEach(element => {
    if (element) {
      ///// Merge internal and external actions
      let mergedActionsByName: BuilderActionData[] = [];

      if (element.actions) {
        const internalActionsByName = defensiveCopy(
          element.actions[actionName]
        );

        if (internalActionsByName) {
          mergedActionsByName = [...internalActionsByName];
        }
      }

      if (configStore.elementsExternalAttrsDepsAndActions.actions) {
        const externalActionsByName =
          configStore.elementsExternalAttrsDepsAndActions.actions[actionName];

        if (externalActionsByName) {
          mergedActionsByName = [
            ...mergedActionsByName,
            ...externalActionsByName
          ];
        }
      }
      //////

      mergedActionsByName.forEach(elementActionData => {
        let actionAlreadyAdded = false;

        actionsWithElements.forEach((actionWithElements, index) => {
          // Merge new element slug into existing action
          if (deepEqual(actionWithElements.actionData, elementActionData)) {
            actionsWithElements[index] = {
              actionData: actionWithElements.actionData,
              elements: [...actionWithElements.elements, element.slug]
            };

            actionAlreadyAdded = true;
          }
        });

        if (!actionAlreadyAdded) {
          actionsWithElements.push({
            actionData: elementActionData,
            elements: [element.slug]
          });
        }
      });
    }
  });

  return actionsWithElements;
}

function loopThroughActions(
  actionName: BuilderActionNames,
  actionCallback: (functionFromScript: Function, action: BuilderAction) => void
) {
  const actions = getUniqueArrayOfActionsByName(actionName);

  for (const action of actions) {
    const actionScript = action.actionData.script;

    if (!actionScript) continue;

    const runAction = async (args: ActionArguments) => {
      const elementId = "elementId" in args && args.elementId;
      const parentId =
        "parentIdOfDeletedElement" in args && args.parentIdOfDeletedElement;

      const maybeElement =
        elementId && findNode(documentStore.document.tree, elementId);
      const maybeParentElement =
        parentId && findNode(documentStore.document.tree, parentId);

      const elementDesign = maybeElement
        ? getDesignForElementFromPreset(maybeElement)
        : {};

      const elementProperties = {
        ...((maybeElement && maybeElement?.data.properties) || {}),
        design: elementDesign
      };

      const tags: Tags = {};

      if (elementId) {
        tags["id"] = elementId;
      }

      if (parentId) {
        tags["parentId"] = parentId;
      }

      if (maybeElement) {
        tags["selector"] = `.${getClassNameForNode(
          maybeElement,
          documentStore.document.documentMeta.id
        )}`;
      }

      if (maybeParentElement) {
        tags["parent_selector"] = `.${getClassNameForNode(
          maybeParentElement,
          documentStore.document.documentMeta.id
        )}`;
      }

      if (maybeElement && actionName !== "onMountedElement") {
        // Run addConditionalDependenciesToHead before the action
        // since an action can have conditional dependencies that have never run before
        // but which condition is now met
        await addBuilderElementConditionalDependenciesToHead(
          getBuilderCanvasDocument(),
          maybeElement,
          tags
        );
      }

      const template = renderTwig(actionScript, elementProperties);

      if (!template) return;

      const actionScriptWithProperties = renderTags(template, tags);

      addOrRerunInlineScriptInHead(getBuilderCanvasDocument(), {
        // Create a tag based off the action code. Ignore the args values to prevent creating a bunch of tags
        tagIdArg: getHash(actionScript),
        code: actionScriptWithProperties,
        origin: `"${actionName}" for element with ID "${elementId}"`
      });
    };

    actionCallback(runAction, action);
  }
}

function doesElementOwnThisAction(
  action: BuilderAction,
  // Element and elementSlug can be from different elements so that's why we have both
  elementSlug: string | undefined,
  element: TreeNode | undefined
) {
  let isCurrentElementAChildOfTheElementWithThisAction = false;
  if (action.actionData.runForAllChildren && element) {
    // Go through all parents and see if the current action belongs to any of them
    // TODO could this be too performance heavy in a big tree? We could "abort" the reduce by adding a callback func
    //  since once we find the parent we don't need to continue
    parentNodesReduce(
      node => {
        if (action.elements.includes(node.data.type)) {
          isCurrentElementAChildOfTheElementWithThisAction = true;
        }

        return [];
      },
      documentStore.document.tree,
      element,
      []
    );

    if (isCurrentElementAChildOfTheElementWithThisAction) {
      return true;
    }
  }

  // Verify this action can run for the element triggering it
  const actionBelongsToItself =
    elementSlug && action.elements.includes(elementSlug);

  return (
    actionBelongsToItself || isCurrentElementAChildOfTheElementWithThisAction
  );
}

// Check if the comparingValue matches any dependency, either as a string or a RegExp
function doesElementOwnThisActionAndDependencyMatch(
  action: BuilderAction,
  comparingValue: string | undefined,
  elementSlug: string | undefined,
  element: TreeNode | undefined
) {
  const dependencies = action.actionData.dependencies;

  if (!doesElementOwnThisAction(action, elementSlug, element)) {
    return false;
  }

  return dependencyMatch(dependencies, comparingValue);
}

export function dependencyMatch(
  dependencies: string[] | undefined,
  comparingValue: string | undefined
) {
  // No dependencies = always run
  if (!dependencies || !dependencies.length || !comparingValue) {
    return true;
  }

  /**
   * "startsWith" will fire actions when the parent control changes.
   * So an action with deps `settings.animations.scrolling_animation` will trigger if we clear the `settings` control
   * Pasting only changes the parent control too, so this will trigger an `onPropChange` action if children controls have it
   */
  return dependencies.some(dependency => {
    return (
      dependency.startsWith(comparingValue) ||
      comparingValue.startsWith(dependency)
    );
  });
}

function isElementHidden(element?: TreeNode) {
  if (!element) return false;

  return (
    element?.data?.properties?.settings?.conditions?.builder_preview === "hide"
  );
}

function registerOnPropertyChange() {
  loopThroughActions("onPropertyChange", (actionFunction, action) => {
    breakdanceStoreActions.register.onPropertyChange(args => {
      const element = findNode(documentStore.document.tree, args.elementId);

      if (isElementHidden(element)) return;

      if (!doesElementOwnThisAction(action, element?.data.type, element)) {
        return;
      }

      const dependencies = action.actionData.dependencies;

      if (
        dependencyMatch(dependencies, args.path) ||
        actionDependencyMatchByValues(
          dependencies,
          args.value,
          args.previousValue,
          args.path
        )
      ) {
        actionFunction(args);
      }
    });
  });
}

function registerOnAfterDeletedElement() {
  loopThroughActions("onAfterDeletedElement", (actionFunction, action) => {
    breakdanceStoreActions.register.onAfterDeletedElement(args => {
      const parentOfDeletedElement = findNode(
        documentStore.document.tree,
        args.parentIdOfDeletedElement
      );

      if (
        doesElementOwnThisActionAndDependencyMatch(
          action,
          parentOfDeletedElement?.data.type,
          args.deletedElementNodeType,
          parentOfDeletedElement
        )
      ) {
        actionFunction(args);
      }
    });
  });
}

function registerOnMovedElement() {
  loopThroughActions("onMovedElement", (actionFunction, action) => {
    breakdanceStoreActions.register.onMovedElement(args => {
      const droppedOnNode = findNode(
        documentStore.document.tree,
        args.droppedOnElementId
      );
      const element = findNode(documentStore.document.tree, args.elementId);

      if (
        doesElementOwnThisActionAndDependencyMatch(
          action,
          droppedOnNode?.data.type,
          element?.data.type,
          element
        )
      ) {
        actionFunction(args);
      }
    });
  });
}

function registerOnMountedElement() {
  loopThroughActions("onMountedElement", (actionFunction, action) => {
    breakdanceStoreActions.register.onMountedElement(args => {
      const element = findNode(documentStore.document.tree, args.elementId);

      if (isElementHidden(element)) return;

      if (
        doesElementOwnThisActionAndDependencyMatch(
          action,
          undefined,
          element?.data.type,
          element
        )
      ) {
        actionFunction(args);
      }
    });
  });
}

function registerOnCreatedElement() {
  loopThroughActions("onCreatedElement", (actionFunction, action) => {
    breakdanceStoreActions.register.onCreatedElement(args => {
      const element = findNode(documentStore.document.tree, args.elementId);

      if (
        doesElementOwnThisActionAndDependencyMatch(
          action,
          undefined,
          element?.data.type,
          element
        )
      ) {
        actionFunction(args);
      }
    });
  });
}

function registerOnBeforeDeletingElement() {
  loopThroughActions("onBeforeDeletingElement", (actionFunction, action) => {
    breakdanceStoreActions.register.onBeforeDeletingElement(args => {
      const element = findNode(documentStore.document.tree, args.elementId);

      if (
        doesElementOwnThisActionAndDependencyMatch(
          action,
          undefined,
          element?.data.type,
          element
        )
      ) {
        actionFunction(args);
      }
    });
  });
}

function registerOnActivatedElement() {
  loopThroughActions("onActivatedElement", (actionFunction, action) => {
    breakdanceStoreActions.register.onActivatedElement(args => {
      const element = findNode(documentStore.document.tree, args.elementId);

      if (isElementHidden(element)) return;

      if (
        doesElementOwnThisActionAndDependencyMatch(
          action,
          undefined,
          element?.data.type,
          element
        )
      ) {
        actionFunction(args);
      }
    });
  });
}
